import React from "react";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import PillPicker from "../PillPicker";
import Summary, { SummaryField, SummaryPills, Actions } from "../Summary";

/**
 * "How to get from today to tomorrow" — choose at least three strategies, with
 * the learner's own two metaphors shown for context.
 */
const MINIMUM_STRATEGIES = 3;

// The stored description restates the two metaphors and then lists all 17
// suggestions inline — wrapped in *…*, which Kramdown renders as one italic
// run-on paragraph rather than a list. Both are shown properly below (the
// metaphors as their own blocks, the suggestions as pills), so the bubble keeps
// only the instruction.
const PROMPT =
  "Choose or type in at least 3 phrases that will help you get from today to tomorrow.";

const FromTodayToTomorrow: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const strategies: string[] = payload.strategies || [];
  const complete = strategies.length >= MINIMUM_STRATEGIES;

  const metaphors = (
    <div className="ix-metaphors">
      <div className="ix-metaphor">
        <div className="ix-label">Today</div>
        <div className="ix-value-lg">{payload.metaphor_today || "—"}</div>
      </div>
      <div className="ix-metaphor">
        <div className="ix-label">Tomorrow</div>
        <div className="ix-value-lg">{payload.metaphor_tomorrow || "—"}</div>
      </div>
    </div>
  );

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser} prompt={PROMPT}>
        <SummaryField>{metaphors}</SummaryField>
        <SummaryField label="How I'll get there">
          <SummaryPills values={strategies} />
        </SummaryField>
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} fallback={PROMPT} />

      <div className="ix-section">{metaphors}</div>

      <div className="ix-section">
        <div className="ix-label">
          At least {MINIMUM_STRATEGIES} things that will get you there
        </div>
        <PillPicker
          selected={strategies}
          options={payload.strategy_options || []}
          onChange={(next) => update({ ...payload, strategies: next })}
          placeholder="Add your own"
        />
      </div>

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={!complete || submitting}
          loading={submitting}
          onClick={submit}
        >
          Submit
        </Button>
      </Actions>
    </div>
  );
};

export default FromTodayToTomorrow;
